import { useEffect, useState } from 'react';
import { api, formatDateTime, formatGbp, formatNumber, kindUsesModel } from '../api';
import { usePolling } from '../auth';
import {
  Badge,
  Card,
  Empty,
  KindBadge,
  Money,
  Notice,
  Percent,
  Spinner,
  StreamChip,
  UsageBar,
} from '../components/ui';
import type {
  DashboardResponse,
  DashboardStream,
  ModelBudgetResponse,
  ModelComparisonRow,
  ModelsResponse,
  StreamKind,
  UniverseEntry,
} from '../types';

type Toast = (message: string, tone?: 'success' | 'error') => void;

export default function Streams({ pushToast }: { pushToast: Toast }) {
  const { data, error, loading, reload } = usePolling<DashboardResponse>(() => api.dashboard(), 45_000);
  const models = usePolling<ModelsResponse>(() => api.models(), null);
  const universe = usePolling<{ universe: UniverseEntry[] }>(() => api.universe(), null);
  const comparison = usePolling<{ rows: ModelComparisonRow[] }>(() => api.modelComparison(), 60_000);
  // Polled briskly: the token bucket refills continuously, so a stale reading is
  // misleading about whether a cycle can run right now.
  const budget = usePolling<ModelBudgetResponse>(() => api.modelBudget(), 10_000);

  if (loading && !data) {
    return (
      <div className="page">
        <Spinner label="Loading streams" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="page">
        <Notice tone="error">{error}</Notice>
      </div>
    );
  }
  if (!data) return null;

  const refreshAll = (): void => {
    reload();
    comparison.reload();
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Streams</h1>
          <p>
            Each stream is an independent portfolio. Pick a different Groq model per AI stream to test
            which one makes better decisions, and add as many as you want to run head to head.
          </p>
        </div>
        <button className="btn" type="button" onClick={refreshAll}>
          Refresh
        </button>
      </div>

      {models.data && !models.data.configured && (
        <Notice tone="warn" title="Groq is not configured">
          {models.data.error ??
            'Add GROQ_API_KEY to .env and restart the server to enable AI and hybrid streams.'}
        </Notice>
      )}

      {budget.data && budget.data.models.length > 0 && <ModelBudgetCard data={budget.data} />}

      <ModelComparison rows={comparison.data?.rows ?? []} />

      {data.streams.map((stream) => (
        <StreamEditor
          key={stream.id}
          stream={stream}
          models={models.data}
          universe={universe.data?.universe ?? []}
          pushToast={pushToast}
          onChanged={refreshAll}
        />
      ))}

      <CreateStream
        models={models.data}
        universe={universe.data?.universe ?? []}
        pushToast={pushToast}
        onCreated={refreshAll}
      />
    </div>
  );
}

/**
 * Live token budget per model.
 *
 * Requests are gated before being sent, so this is mostly reassurance rather than
 * something to act on. It matters when a stream reports being skipped: this shows
 * whether that was budget pressure and how long until it clears.
 */
function ModelBudgetCard({ data }: { data: ModelBudgetResponse }) {
  return (
    <Card
      title="Model rate limit budget"
      subtitle="read from Groq's own headers, enforced before each request"
    >
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Model</th>
              <th className="num">Tokens left</th>
              <th>Usage this window</th>
              <th className="num">Window</th>
              <th className="num">Requests left</th>
              <th className="num">Calls</th>
              <th className="num">Throttled</th>
              <th>Next call</th>
            </tr>
          </thead>
          <tbody>
            {data.models.map((entry) => {
              const usedPct = Math.min(100, Math.max(0, entry.tokens.utilisationPct));
              return (
                <tr key={entry.model}>
                  <td className="mono-small">{entry.model}</td>
                  <td className="num">
                    {formatNumber(entry.tokens.remaining, 0)}
                    <div className="mono-small">of {formatNumber(entry.tokens.limit, 0)}</div>
                  </td>
                  <td style={{ minWidth: 140 }}>
                    <div className="bar-track">
                      <div
                        className="bar-fill"
                        style={{
                          width: `${usedPct}%`,
                          background:
                            usedPct > 85
                              ? 'var(--red)'
                              : usedPct > 60
                                ? 'var(--amber)'
                                : 'var(--green)',
                        }}
                      />
                    </div>
                    <div className="mono-small">{usedPct.toFixed(0)}% used</div>
                  </td>
                  <td className="num">{Math.round(entry.tokens.windowMs / 1000)}s</td>
                  <td className="num">
                    {formatNumber(entry.requests.remaining, 0)}
                    <div className="mono-small">
                      of {formatNumber(entry.requests.limit, 0)} per{' '}
                      {entry.requests.windowMs >= 3_600_000
                        ? `${Math.round(entry.requests.windowMs / 3_600_000)}h`
                        : `${Math.round(entry.requests.windowMs / 1000)}s`}
                    </div>
                  </td>
                  <td className="num">{entry.calls}</td>
                  <td className="num">
                    {entry.throttledCalls === 0 ? (
                      '0'
                    ) : (
                      <>
                        {entry.throttledCalls}
                        <div className="mono-small">
                          longest {(entry.longestWaitMs / 1000).toFixed(1)}s
                        </div>
                      </>
                    )}
                  </td>
                  <td>
                    {entry.dailyLimitUntil ? (
                      <Badge tone="red">day spent until {formatDateTime(entry.dailyLimitUntil)}</Badge>
                    ) : entry.headroom.canProceedNow ? (
                      <Badge tone="green">ready</Badge>
                    ) : entry.headroom.waitMs < 0 ? (
                      <Badge tone="red">over budget</Badge>
                    ) : (
                      <Badge tone="amber">
                        waits {(entry.headroom.waitMs / 1000).toFixed(1)}s
                      </Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--text-faint)' }}>
        {data.note} A stream skipped for the per-minute budget runs again next cycle. If a model's
        daily allowance (200,000 tokens on the free tier) is spent, its streams sit out until Groq
        resets it; stops still execute.
      </p>
    </Card>
  );
}

function ModelComparison({ rows }: { rows: ModelComparisonRow[] }) {
  if (rows.length === 0) return null;
  return (
    <Card
      title="Model scoreboard"
      subtitle="P&L against latency and token spend, so a marginal win at 5x the cost is visible"
    >
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Stream</th>
              <th>Approach</th>
              <th>Model</th>
              <th className="num">Trades</th>
              <th className="num">Closed</th>
              <th className="num">Win rate</th>
              <th className="num">Realised P&amp;L</th>
              <th className="num">Costs</th>
              <th className="num">Model calls</th>
              <th className="num">Failures</th>
              <th className="num">Avg latency</th>
              <th className="num">Tokens</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.streamId}>
                <td>
                  <strong>{row.streamName}</strong>
                </td>
                <td>
                  <KindBadge kind={row.strategy} />
                </td>
                <td className="mono-small">{row.model ?? 'deterministic'}</td>
                <td className="num">{row.trades}</td>
                <td className="num">{row.closedTrades}</td>
                <td className="num">
                  {row.winRatePct === null ? '—' : `${row.winRatePct.toFixed(0)}%`}
                </td>
                <td className="num">
                  <Money value={row.realisedPnlGbp} showSign />
                </td>
                <td className="num">{formatGbp(row.feesPaidGbp)}</td>
                <td className="num">{row.aiCalls}</td>
                <td className="num">
                  {row.aiFailures > 0 ? <span className="neg">{row.aiFailures}</span> : '0'}
                </td>
                <td className="num">
                  {row.avgLatencyMs === null ? '—' : `${row.avgLatencyMs}ms`}
                </td>
                <td className="num">
                  {row.totalTokens === 0 ? '—' : formatNumber(row.totalTokens, 0)}
                  {row.avgTokensPerCall !== null && row.avgTokensPerCall > 0 && (
                    <div className="mono-small">{formatNumber(row.avgTokensPerCall, 0)}/call</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

interface StockBasketBuilderProps {
  universe: UniverseEntry[];
  symbols: string[];
  onChange: (symbols: string[]) => void;
  scope: 'all' | 'custom';
  onScopeChange: (scope: 'all' | 'custom') => void;
  idPrefix: string;
}

function StockBasketBuilder({
  universe,
  symbols,
  onChange,
  scope,
  onScopeChange,
  idPrefix,
}: StockBasketBuilderProps) {
  const [tickerInput, setTickerInput] = useState('');

  const addTicker = (raw: string) => {
    const clean = raw.trim().toUpperCase();
    if (!clean) return;
    if (!symbols.includes(clean)) {
      onChange([...symbols, clean]);
    }
    setTickerInput('');
  };

  const removeTicker = (target: string) => {
    onChange(symbols.filter((s) => s !== target));
  };

  const addAllUs = () => {
    const usSymbols = universe.filter((u) => u.exchange === 'US').map((u) => u.symbol);
    const merged = Array.from(new Set([...symbols, ...usSymbols]));
    onChange(merged);
  };

  const addAllLse = () => {
    const lseSymbols = universe.filter((u) => u.exchange === 'LSE').map((u) => u.symbol);
    const merged = Array.from(new Set([...symbols, ...lseSymbols]));
    onChange(merged);
  };

  const addBigTech = () => {
    const tech = ['AAPL', 'MSFT', 'NVDA', 'AMZN', 'GOOGL', 'META', 'TSLA'];
    const merged = Array.from(new Set([...symbols, ...tech]));
    onChange(merged);
  };

  return (
    <div style={{ marginTop: 8, marginBottom: 12 }}>
      <div className="btn-row" style={{ marginBottom: 10 }}>
        <button
          type="button"
          className={`btn btn-sm ${scope === 'all' ? 'btn-primary' : ''}`}
          onClick={() => onScopeChange('all')}
        >
          All universe stocks ({universe.length || 40})
        </button>
        <button
          type="button"
          className={`btn btn-sm ${scope === 'custom' ? 'btn-primary' : ''}`}
          onClick={() => onScopeChange('custom')}
        >
          Specific stocks ({symbols.length})
        </button>
      </div>

      {scope === 'all' ? (
        <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--ink-dim)', fontFamily: 'var(--mono)' }}>
          This stream will scan all ~{universe.length || 40} instruments from the LSE and US universe on each decision cycle.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="inline-form" style={{ alignItems: 'flex-end' }}>
            <div className="field" style={{ flex: '1 1 180px' }}>
              <label htmlFor={`${idPrefix}-ticker`}>Add ticker symbol</label>
              <input
                id={`${idPrefix}-ticker`}
                type="text"
                placeholder="e.g. AAPL or SHEL.L"
                value={tickerInput}
                onChange={(e) => setTickerInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTicker(tickerInput);
                  }
                }}
              />
            </div>
            <button
              className="btn"
              type="button"
              disabled={!tickerInput.trim()}
              onClick={() => addTicker(tickerInput)}
            >
              Add ticker
            </button>

            <div className="field" style={{ flex: '1 1 240px' }}>
              <label htmlFor={`${idPrefix}-universe-select`}>Or select from universe</label>
              <select
                id={`${idPrefix}-universe-select`}
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) {
                    addTicker(e.target.value);
                    e.target.value = '';
                  }
                }}
              >
                <option value="" disabled>
                  Choose a stock...
                </option>
                {universe.map((entry) => (
                  <option key={entry.symbol} value={entry.symbol}>
                    {entry.symbol} — {entry.name} ({entry.exchange})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="btn-row" style={{ gap: 6 }}>
            <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-faint)', alignSelf: 'center' }}>
              Presets:
            </span>
            <button type="button" className="btn btn-sm" onClick={addBigTech}>
              + US Big Tech (7)
            </button>
            <button type="button" className="btn btn-sm" onClick={addAllUs}>
              + All US ({universe.filter((u) => u.exchange === 'US').length})
            </button>
            <button type="button" className="btn btn-sm" onClick={addAllLse}>
              + All LSE ({universe.filter((u) => u.exchange === 'LSE').length})
            </button>
            {symbols.length > 0 && (
              <button type="button" className="btn btn-sm" onClick={() => onChange([])}>
                Clear all
              </button>
            )}
          </div>

          <div
            style={{
              padding: '10px 12px',
              background: 'var(--bg-inset)',
              border: '1px solid var(--hair-strong)',
              minHeight: 44,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              alignItems: 'center',
            }}
          >
            {symbols.length === 0 ? (
              <span style={{ color: 'var(--ink-faint)', fontSize: 12, fontFamily: 'var(--mono)' }}>
                No stocks added yet. Type a ticker above or select from the universe list.
              </span>
            ) : (
              symbols.map((sym) => (
                <span
                  key={sym}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '3px 8px',
                    background: 'var(--bg-panel-2)',
                    border: '1px solid var(--hair-strong)',
                    fontFamily: 'var(--mono)',
                    fontSize: 12,
                    color: 'var(--ink)',
                  }}
                >
                  <strong>{sym}</strong>
                  <button
                    type="button"
                    onClick={() => removeTicker(sym)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--ink-dim)',
                      cursor: 'pointer',
                      padding: '0 2px',
                      fontSize: 14,
                      lineHeight: 1,
                    }}
                    title={`Remove ${sym}`}
                  >
                    ×
                  </button>
                </span>
              ))
            )}
          </div>
          <span className="field-hint">
            Only these stocks (plus any held positions) will be analyzed and traded by this stream.
          </span>
        </div>
      )}
    </div>
  );
}

function StreamEditor({
  stream,
  models,
  universe,
  pushToast,
  onChanged,
}: {
  stream: DashboardStream;
  models: ModelsResponse | null;
  universe: UniverseEntry[];
  pushToast: Toast;
  onChanged: () => void;
}) {
  const [model, setModel] = useState(stream.model ?? '');
  const [depositAmount, setDepositAmount] = useState('1000');
  const [busy, setBusy] = useState<string | null>(null);

  const [editingStocks, setEditingStocks] = useState(false);
  const [stockScope, setStockScope] = useState<'all' | 'custom'>(
    stream.symbols && stream.symbols.length > 0 ? 'custom' : 'all',
  );
  const [streamSymbols, setStreamSymbols] = useState<string[]>(stream.symbols ?? []);

  // Keep state in sync when polling brings changed values.
  useEffect(() => {
    setModel(stream.model ?? '');
  }, [stream.model]);

  useEffect(() => {
    setStockScope(stream.symbols && stream.symbols.length > 0 ? 'custom' : 'all');
    setStreamSymbols(stream.symbols ?? []);
  }, [stream.symbols]);

  const usesModel = kindUsesModel(stream.kind);
  const modelOptions = models?.models ?? [];
  // A model set in .env but missing from the catalogue should still be shown,
  // otherwise the select would silently drop the current value.
  const showsUnknownModel = usesModel && model !== '' && !modelOptions.some((entry) => entry.id === model);

  async function act(label: string, action: () => Promise<unknown>): Promise<void> {
    setBusy(label);
    try {
      await action();
      pushToast(`${stream.name}: ${label} applied.`);
      onChanged();
    } catch (err) {
      pushToast(err instanceof Error ? err.message : `${label} failed`, 'error');
    } finally {
      setBusy(null);
    }
  }

  async function saveStocks(): Promise<void> {
    if (stockScope === 'custom' && streamSymbols.length === 0) {
      pushToast('Add at least one stock to consider, or select "All universe stocks".', 'error');
      return;
    }
    const finalSymbols = stockScope === 'all' ? null : streamSymbols;
    await act('stock consideration list updated', () =>
      api.updateStream(stream.id, { symbols: finalSymbols }),
    );
    setEditingStocks(false);
  }

  return (
    <Card
      title={<StreamChip name={stream.name} colour={stream.colour} />}
      actions={
        <>
          <KindBadge kind={stream.kind} />
          <Badge tone={stream.enabled ? 'green' : 'grey'}>{stream.enabled ? 'Enabled' : 'Disabled'}</Badge>
          {stream.halted && <Badge tone="red">Halted</Badge>}
        </>
      }
    >
      {stream.halted && (
        <Notice tone="warn" title="Trading halted">
          {stream.haltReason ?? 'No reason recorded.'} Resume it below to let it trade again.
        </Notice>
      )}

      <div className="grid grid-4" style={{ marginBottom: 14 }}>
        <div>
          <div className="stat-label">Equity</div>
          <div className="stat-value" style={{ fontSize: 17 }}>
            {formatGbp(stream.equityGbp)}
          </div>
        </div>
        <div>
          <div className="stat-label">P&amp;L</div>
          <div className="stat-value" style={{ fontSize: 17 }}>
            <Money value={stream.totalPnlGbp} showSign />
          </div>
          <div className="stat-foot">
            <Percent value={stream.totalReturnPct} decimals={3} />
          </div>
        </div>
        <div>
          <div className="stat-label">Deposited</div>
          <div className="stat-value" style={{ fontSize: 17 }}>
            {formatGbp(stream.depositedGbp)}
          </div>
        </div>
        <div>
          <div className="stat-label">Trades today</div>
          <div className="stat-value" style={{ fontSize: 17 }}>
            {stream.dailyLimit.used}/{stream.dailyLimit.limit}
          </div>
          <UsageBar used={stream.dailyLimit.used} limit={stream.dailyLimit.limit} colour={stream.colour} />
        </div>
      </div>

      {stream.kind === 'OPTIONS' ? (
        // An options stream trades its underlying's contracts, not the stock universe,
        // so the stock picker would only break it.
        <div
          style={{
            marginBottom: 14,
            padding: '10px 14px',
            background: 'var(--bg-inset)',
            border: '1px solid var(--hair)',
          }}
        >
          <span className="stat-label" style={{ marginRight: 8 }}>
            Underlying:
          </span>
          {(stream.symbols && stream.symbols.length > 0 ? stream.symbols : ['SPY']).map((sym) => (
            <span key={sym} className="tag" style={{ fontSize: 11, marginRight: 6 }}>
              {sym}
            </span>
          ))}
          <div className="field-hint" style={{ marginTop: 8 }}>
            Sells one cash-secured, at-the-money monthly put at a time and holds it to expiry, as the
            Cboe PUT index does. Prices are model prices (Black-Scholes at the live VIX); contracts may
            be fractional.
          </div>
        </div>
      ) : (
      <div
        style={{
          marginBottom: 14,
          padding: '10px 14px',
          background: 'var(--bg-inset)',
          border: '1px solid var(--hair)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span className="stat-label" style={{ marginRight: 8 }}>
              Stocks to consider:
            </span>
            {!stream.symbols || stream.symbols.length === 0 ? (
              <span className="tag tag-amber" style={{ fontSize: 11 }}>
                All universe stocks ({universe.length || 40} instruments)
              </span>
            ) : (
              <span style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--ink)' }}>
                <strong>{stream.symbols.length}</strong> specific stock{stream.symbols.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <button
            className="btn btn-sm"
            type="button"
            onClick={() => setEditingStocks(!editingStocks)}
          >
            {editingStocks ? 'Cancel' : 'Manage stocks'}
          </button>
        </div>

        {!editingStocks && stream.symbols && stream.symbols.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {stream.symbols.map((sym) => (
              <span key={sym} className="tag" style={{ fontSize: 11 }}>
                {sym}
              </span>
            ))}
          </div>
        )}

        {editingStocks && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--hair)' }}>
            <StockBasketBuilder
              universe={universe}
              symbols={streamSymbols}
              onChange={setStreamSymbols}
              scope={stockScope}
              onScopeChange={setStockScope}
              idPrefix={`edit-stream-${stream.id}`}
            />
            <div style={{ marginTop: 10 }}>
              <button
                className="btn btn-primary btn-sm"
                type="button"
                disabled={busy !== null}
                onClick={() => void saveStocks()}
              >
                {busy === 'stock consideration list updated' ? <Spinner /> : 'Save stocks'}
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {usesModel && (
        <div className="inline-form" style={{ marginBottom: 12 }}>
          <div className="field" style={{ flex: '1 1 340px' }}>
            <label htmlFor={`model-${stream.id}`}>Groq model</label>
            <select
              id={`model-${stream.id}`}
              value={model}
              onChange={(event) => setModel(event.target.value)}
              disabled={!models?.configured}
            >
              {showsUnknownModel && <option value={model}>{model} (not in catalogue)</option>}
              {modelOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.id}
                  {option.contextWindow ? ` · ${(option.contextWindow / 1000).toFixed(0)}k ctx` : ''}
                  {option.usedByStreams.length > 0 ? ` · used by ${option.usedByStreams.join(', ')}` : ''}
                </option>
              ))}
            </select>
            <span className="field-hint">
              Changing this affects future cycles only. Existing trades keep the model they were made
              with, so history stays honest.
            </span>
          </div>
          <button
            className="btn"
            type="button"
            disabled={busy !== null || model === (stream.model ?? '') || !models?.configured}
            onClick={() => void act('model change', () => api.updateStream(stream.id, { model }))}
          >
            {busy === 'model change' ? <Spinner /> : 'Save model'}
          </button>
        </div>
      )}

      <div className="inline-form" style={{ marginBottom: 12 }}>
        <div className="field" style={{ maxWidth: 160 }}>
          <label htmlFor={`deposit-${stream.id}`}>Add funds (GBP)</label>
          <input
            id={`deposit-${stream.id}`}
            type="number"
            min="1"
            step="100"
            value={depositAmount}
            onChange={(event) => setDepositAmount(event.target.value)}
          />
        </div>
        <button
          className="btn"
          type="button"
          disabled={busy !== null}
          onClick={() =>
            void act('deposit', () => api.deposit(stream.id, Number(depositAmount)))
          }
        >
          Deposit
        </button>
        <button
          className="btn"
          type="button"
          disabled={busy !== null}
          onClick={() => void act('withdrawal', () => api.withdraw(stream.id, Number(depositAmount)))}
        >
          Withdraw
        </button>
      </div>

      <div className="btn-row">
        <button
          className="btn"
          type="button"
          disabled={busy !== null}
          onClick={() =>
            void act(stream.enabled ? 'disable' : 'enable', () =>
              api.updateStream(stream.id, { enabled: !stream.enabled }),
            )
          }
        >
          {stream.enabled ? 'Disable' : 'Enable'}
        </button>
        {stream.halted && (
          <button
            className="btn"
            type="button"
            disabled={busy !== null}
            onClick={() => void act('resume', () => api.resumeStream(stream.id))}
          >
            Resume trading
          </button>
        )}
        <button
          className="btn btn-danger"
          type="button"
          disabled={busy !== null}
          onClick={() => {
            if (
              !window.confirm(
                `Reset ${stream.name}? This permanently deletes its positions, trades, decisions and equity history, then restores the starting balance.`,
              )
            ) {
              return;
            }
            void act('reset', () => api.resetStream(stream.id));
          }}
        >
          Reset stream
        </button>
        <button
          className="btn btn-danger"
          type="button"
          disabled={busy !== null}
          onClick={() => {
            if (!window.confirm(`Delete ${stream.name} and all of its history? This cannot be undone.`)) {
              return;
            }
            void act('delete', () => api.deleteStream(stream.id));
          }}
        >
          Delete
        </button>
      </div>
    </Card>
  );
}

function CreateStream({
  models,
  universe,
  pushToast,
  onCreated,
}: {
  models: ModelsResponse | null;
  universe: UniverseEntry[];
  pushToast: Toast;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<StreamKind>('AI');
  const [model, setModel] = useState('');
  const [funding, setFunding] = useState('10000');
  const [busy, setBusy] = useState(false);

  const [stockScope, setStockScope] = useState<'all' | 'custom'>('all');
  const [selectedSymbols, setSelectedSymbols] = useState<string[]>([]);

  const needsModel = kindUsesModel(kind);
  const isOptions = kind === 'OPTIONS';
  const modelOptions = models?.models ?? [];
  const effectiveModel = model || models?.defaultModel || '';

  async function create(): Promise<void> {
    if (name.trim() === '') {
      pushToast('Give the stream a name.', 'error');
      return;
    }
    if (!isOptions && stockScope === 'custom' && selectedSymbols.length === 0) {
      pushToast('Add at least one stock to consider, or select "All universe stocks".', 'error');
      return;
    }
    setBusy(true);
    try {
      await api.createStream({
        name: name.trim(),
        kind,
        ...(needsModel ? { model: effectiveModel } : {}),
        fundingGbp: Number(funding),
        // The server gives an options stream its underlying (SPY).
        symbols: isOptions || stockScope === 'all' ? null : selectedSymbols,
      });
      pushToast(
        isOptions
          ? `Stream "${name.trim()}" created: a cash-secured SPY put-write.`
          : `Stream "${name.trim()}" created with ${
              stockScope === 'all' ? 'all universe' : `${selectedSymbols.length} specific`
            } stocks.`,
      );
      setName('');
      setSelectedSymbols([]);
      setStockScope('all');
      onCreated();
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'Could not create the stream', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="Add a stream"
      subtitle="create a new competitor with custom funding and specific stocks to consider"
    >
      <div className="inline-form">
        <div className="field" style={{ flex: '1 1 180px' }}>
          <label htmlFor="new-name">Name</label>
          <input
            id="new-name"
            type="text"
            value={name}
            placeholder="e.g. AI (gpt-oss-120b)"
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="field" style={{ maxWidth: 190 }}>
          <label htmlFor="new-kind">Approach</label>
          <select
            id="new-kind"
            value={kind}
            onChange={(event) => setKind(event.target.value as StreamKind)}
          >
            <option value="AI">Groq AI only</option>
            <option value="PATTERN">Pattern matching only</option>
            <option value="HYBRID">AI + Pattern (both must agree)</option>
            <option value="MOMENTUM">Momentum (12-1 ranking)</option>
            <option value="OPTIONS">Options (SPY put-write)</option>
          </select>
        </div>
        {needsModel && (
          <div className="field" style={{ flex: '1 1 260px' }}>
            <label htmlFor="new-model">Model</label>
            <select
              id="new-model"
              value={effectiveModel}
              onChange={(event) => setModel(event.target.value)}
              disabled={!models?.configured}
            >
              {modelOptions.length === 0 && <option value="">no models available</option>}
              {modelOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.id}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="field" style={{ maxWidth: 150 }}>
          <label htmlFor="new-funding">Funding (GBP)</label>
          <input
            id="new-funding"
            type="number"
            min="1"
            step="500"
            value={funding}
            onChange={(event) => setFunding(event.target.value)}
          />
        </div>
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => void create()}
          disabled={busy || (needsModel && !models?.configured)}
        >
          {busy ? <Spinner /> : 'Create stream'}
        </button>
      </div>

      {isOptions ? (
        <p style={{ margin: '14px 0 0', fontSize: 12, color: 'var(--text-faint)' }}>
          Trades SPY options only: one cash-secured, at-the-money monthly put at a time, held to expiry
          and settled at intrinsic value. One real SPY contract is secured by 100 times the strike,
          tens of thousands of pounds, so smaller streams trade fractional contracts.
        </p>
      ) : (
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--hair)' }}>
          <label
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: 'var(--ink-dim)',
              display: 'block',
              marginBottom: 4,
            }}
          >
            Stocks to Consider
          </label>
          <StockBasketBuilder
            universe={universe}
            symbols={selectedSymbols}
            onChange={setSelectedSymbols}
            scope={stockScope}
            onScopeChange={setStockScope}
            idPrefix="new-stream"
          />
        </div>
      )}

      {needsModel && !models?.configured && (
        <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--text-faint)' }}>
          AI and hybrid streams need a Groq API key. Pattern, momentum and options streams can be
          created without one.
        </p>
      )}
      <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--text-faint)' }}>
        Fund new streams with the same amount as the existing ones if you want the comparison to stay
        like for like.
      </p>
      {models?.configured === false && <Empty>Model list unavailable.</Empty>}
    </Card>
  );
}
